import React, { useState, useEffect } from 'react'
import { Document } from '@contentful/rich-text-types'
import { OptInBody, OptInText, SignUpComplete } from '@pcln/email-signup'
import { ContentfulRichText, IOptInType } from '@pcln/contentful-components'
import LayoutComponentWrapper from '@/components/LayoutComponentWrapper'
import styled from 'styled-components'
import analytics from '@/shared-utils/analytics'
import { Box } from 'pcln-design-system'
import useContentfulVariation from '@/hooks/useContentfulVariation'
import CONTENT from './content'
import submitEmail from './utils'

type EmailSignup = {
  sourceId: string
  category?: string
}

function mapContentfulContent(optIn: IOptInType) {
  const { url, label, analyticsTracking } = optIn.callToAction ?? {}
  const { placeholder, error } = optIn?.input?.[0] ?? {}
  const {
    headline,
    subheadline,
    successHeadline,
    alertId,
    channelType,
    contactSourceType,
    googleAnalyticsTracking
  } = optIn
  return {
    headline,
    subheadline,
    successHeadline,
    placeholder,
    invalidEmailError: new Error(error),
    buttonText: label,
    url,
    alertId,
    channelType,
    contactSourceType,
    googleAnalyticsTrackingOnView: {
      category: googleAnalyticsTracking?.category || 'email opt-in',
      action: googleAnalyticsTracking?.action,
      label: googleAnalyticsTracking?.label
    },
    googleAnalyticsTrackingOnClick: {
      category: analyticsTracking?.category,
      action: analyticsTracking?.action,
      label: analyticsTracking?.label
    }
  }
}

const RichText = styled(ContentfulRichText)`
  margin: 0px;
`

export default function EmailSignUp({ sourceId, category = '' }: EmailSignup) {
  const optIn = useContentfulVariation<IOptInType>('emailOptIn')
  const hasContentfulData = optIn != null

  // TODO: Remove this useEffect after 6 months. Ticket: https://priceline.atlassian.net/browse/SITE-21695
  useEffect(() => {
    localStorage.removeItem('hideEmailOptIn')
  }, [])

  const {
    headline,
    subheadline,
    successHeadline,
    placeholder,
    invalidEmailError,
    buttonText,
    url = CONTENT.url,
    alertId,
    channelType,
    contactSourceType,
    googleAnalyticsTrackingOnView,
    googleAnalyticsTrackingOnClick
  } = hasContentfulData ? mapContentfulContent(optIn) : CONTENT

  const {
    action: gaActionOnView,
    category: gaCategoryOnView,
    label: gaLabelOnView
  } = googleAnalyticsTrackingOnView

  const [isLoading, setIsLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState<Error | undefined>(undefined)
  const [emailEntered, setEmailEntered] = useState<string>('')

  async function submitHandler({ emailAddress }: { emailAddress: string }) {
    setIsLoading(true)
    setEmailEntered(emailAddress)
    try {
      await submitEmail(
        url,
        emailAddress,
        sourceId,
        alertId,
        channelType,
        contactSourceType
      )
      setSuccess(true)
      analytics.fireEvent({
        action: googleAnalyticsTrackingOnClick.action || 'Email Confirm',
        category: googleAnalyticsTrackingOnClick.category || category,
        label: googleAnalyticsTrackingOnClick.label || 'Thank You'
      })
    } catch (err) {
      if (err instanceof Error) {
        setError(err)
      }
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (gaActionOnView) {
      analytics.fireEvent({
        action: gaActionOnView,
        category: gaCategoryOnView,
        label: gaLabelOnView
      })
    }
  }, [gaActionOnView, gaCategoryOnView, gaLabelOnView])

  return (
    <LayoutComponentWrapper>
      <Box>
        <OptInBody
          success={success}
          apiErrorMessage={error}
          invaildEmailErrorMessage={invalidEmailError}
          isLoading={isLoading}
          submitEmail={email => submitHandler(email)}
          buttonText={buttonText}
          placeholder={placeholder}
          borderRadius="lg"
          emailInputMinWidth={[296, null, null, 200, 400]}
          formControlSize="large"
          successTemplate={
            <SignUpComplete
              headline={
                successHeadline && (
                  // SITE-21053 should remove this assertion
                  <RichText body={successHeadline as Document} />
                )
              }
              subheadline={emailEntered}
            />
          }
        >
          <OptInText
            subheadlineFontSize={2}
            headline={headline && <RichText body={headline} />}
            subheadline={subheadline && <RichText body={subheadline} />}
            spaceBetweenHeadlines={1}
          />
        </OptInBody>
      </Box>
    </LayoutComponentWrapper>
  )
}
